import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { computed, inject, Injectable, linkedSignal, type Signal, signal, type WritableSignal } from '@angular/core';

import { firstValueFrom } from 'rxjs';

import { toAppError } from '../../../shared/utils/app-error.util';
import type { AppError } from '../../interfaces';
import { ClusterService } from '../context/cluster.service';
import { translateNow, translationVersion } from '../i18n/translate-now';

export interface CategoryTreeNode {
  name: string;
  categoryReference: string;
  childCategories: CategoryTreeNode[];
  level: number;
}

export interface BuildCategoriesTreeResponse {
  message: string;
  operationPayload: CategoryTreeNode[];
}

/** A failure kept as a catalog key (or literal text) so it can be shown in the language chosen later. */
interface FailureText {
  key?: string;
  literal?: string;
}

/**
 * Same choice as `getErrorMessage`, but it keeps the catalog key instead of the resolved text:
 * errors raised by app code keep their own message, unknown values use the fallback key.
 */
function toFailureText(err: unknown, fallbackKey: string): FailureText {
  const appError = toAppError(err);
  if (appError.source === 'client') {
    if (appError.messageKey) return { key: appError.messageKey };
    return err instanceof Error && err.message ? { literal: err.message } : { key: fallbackKey };
  }
  if (appError.source === 'unknown' && !(err instanceof HttpErrorResponse)) return { key: fallbackKey };
  return appError.messageKey ? { key: appError.messageKey } : { literal: appError.message };
}

/** Writable text derived from a stored failure; a dismissed failure stays dismissed after a language switch. */
function failureText(failure: Signal<FailureText | null>): WritableSignal<string | null> {
  return linkedSignal<{ failure: FailureText | null; version: number }, string | null>({
    source: () => ({ failure: failure(), version: translationVersion() }),
    computation: (source, previous) => {
      if (previous && previous.value === null && previous.source.failure === source.failure) return null;
      const { failure: f } = source;
      if (!f) return null;
      return f.key ? translateNow(f.key) : (f.literal ?? null);
    },
  });
}

/** Fury backend — primary service for new features (category tree, logistic clients). */
@Injectable({ providedIn: 'root' })
export class FuryApiService {
  private readonly http = inject(HttpClient);
  private readonly clusterService = inject(ClusterService);

  readonly previewLoading = signal(false);
  private readonly previewFailure = signal<FailureText | null>(null);
  /** Text of the last preview failure in the active language; `set(null)` dismisses it for good. */
  readonly previewError = failureText(this.previewFailure);

  readonly createLoading = signal(false);
  private readonly createFailure = signal<FailureText | null>(null);
  /** Text of the last creation failure in the active language; `set(null)` dismisses it for good. */
  readonly createError = failureText(this.createFailure);

  private readonly baseUrl = computed(() => this.clusterService.furyBaseUrl());

  /** GET bff/build-categories-tree?fileUrl=... — parses a Google Sheet into a category tree preview, without persisting anything. */
  async buildCategoriesTree(fileUrl: string): Promise<CategoryTreeNode[] | null> {
    this.previewLoading.set(true);
    this.previewFailure.set(null);
    try {
      const base = this.requireBaseUrl();
      const url = `${base}bff/build-categories-tree`;
      const params = new HttpParams().set('fileUrl', fileUrl);
      const res = await firstValueFrom(
        this.http.get<BuildCategoriesTreeResponse>(url, { params }),
      );
      return res.operationPayload ?? null;
    } catch (err) {
      this.previewFailure.set(toFailureText(err, 'errors.categoryTreePreview'));
      return null;
    } finally {
      this.previewLoading.set(false);
    }
  }

  /** POST categories/create-categories-tree — creates the category tree for a client from the same sheet. */
  async createCategoriesTree(clientId: string, fileUrl: string): Promise<unknown | null> {
    this.createLoading.set(true);
    this.createFailure.set(null);
    try {
      const base = this.requireBaseUrl();
      const url = `${base}categories/create-categories-tree`;
      return await firstValueFrom(this.http.post<unknown>(url, { clientId, fileUrl }));
    } catch (err) {
      this.createFailure.set(toFailureText(err, 'errors.categoryTreeCreate'));
      return null;
    } finally {
      this.createLoading.set(false);
    }
  }

  private requireBaseUrl(): string {
    const url = this.baseUrl();
    if (!url) {
      const key = 'common.datasource.noFuryBaseUrl';
      const error: AppError = { message: translateNow(key), messageKey: key, source: 'client', retryable: false };
      throw error;
    }
    return url;
  }
}
